import React, { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Box, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import MDButton from 'components/MDButton';
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import KeywordsInput from 'layouts/tables/Product_Details/KeywordsInput';
import EditVendorValidationSchema from './edit_service_schema';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const EditServiceModal = ({ open, handleClose, editInput }) => {
  const [servicesType, setServicesType] = useState([]);
  const [allSpecializations, setAllSpecializations] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const parsedSubservices = useCallback(() => {
    try {
      const raw = JSON.parse(editInput.subservices || '{}');
      const parsed = {};
      Object.entries(raw).forEach(([key, valArr]) => {
        if (Array.isArray(valArr)) {
          parsed[key] = valArr.flatMap(item => item.split(',').map(s => s.trim().toLowerCase()));
        } else if (typeof valArr === 'string') {
          parsed[key] = valArr.split(',').map(s => s.trim().toLowerCase());
        }
      });
      return parsed;
    } catch {
      return {};
    }
  }, [editInput]);

  const initialServiceTypes = Object.keys(parsedSubservices());
  const initialSpecializations = Object.values(parsedSubservices()).flat();

  const [filteredSpecializations, setFilteredSpecializations] = useState(initialSpecializations);

  const [sameAsHome, setSameAsHome] = useState(
    editInput.worklocation === editInput.homeaddress && !!editInput.worklocation
  );

  const fetchAllServiceTypesWithSpecializations = useCallback(async () => {
    try {
      const response = await api.get('/service_management/service_specializations/get_all_active_service_types_with_specializations');
      const result = response.data.data || [];
      const types = result.map((item) => ({
        id: item.id,
        type_name: item.service_type,
        specializations: item.specializations ? item.specializations.map(s => s.toLowerCase()) : [],
      }));
      setServicesType(types);
      setAllSpecializations(types);
    } catch (error) {
      console.error('Error fetching service types and specializations:', error);
    }
  }, []);

  useEffect(() => {
    fetchAllServiceTypesWithSpecializations();
  }, [fetchAllServiceTypesWithSpecializations]);

  const formik = useFormik({
    initialValues: {
      ...editInput,
      services_type: initialServiceTypes,
      subservices: initialSpecializations,
      keywords: editInput.keywords ? editInput.keywords.split(',') : [],
      // --- ⚠️ MODIFICATION: Pre-filling the password field ---
      // This is generally not recommended for security reasons.
      // The standard practice is to leave this field blank and only update if the user enters a new value.
      password: editInput.password || '',
    },
    validationSchema: EditVendorValidationSchema,
    onSubmit: async (values) => {
      try {
        const formattedSubservices = {};
        values.services_type.forEach((type) => {
          const matchedSpecs = allSpecializations.find((s) => s.type_name === type)?.specializations || [];
          const selected = values.subservices
            .filter((spec) => matchedSpecs.includes(spec))
            .map((s) => s.trim())
            .filter(Boolean);
          if (selected.length > 0) {
            formattedSubservices[type] = [selected.join(',')];
          }
        });

        const finalData = {
          ...values,
          subservices: JSON.stringify(formattedSubservices),
          keywords: values.keywords.join(','),
        };
        
        // This logic is still useful. If the user clears the pre-filled password,
        // it won't send an empty password to the backend. It will just not update it.
        if (!finalData.password) {
          delete finalData.password;
        }

        const response = await api.put('/services_register/update_service_provider', finalData);

        if (response.data.status === 1) {
          Swal.fire({
            title: 'Service Provider Updated Successfully!',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false,
          });
          handleClose();
        } else {
          Swal.fire({
            title: 'Update Failed',
            text: response.data.message || 'Something went wrong.',
            icon: 'error',
          });
        }
      } catch (error) {
        console.error('Error updating vendor:', error);
        Swal.fire({
          title: 'Server Error',
          text: 'An error occurred while updating. Please try again.',
          icon: 'error',
        });
      }
    },
    enableReinitialize: true,
  });

  // Effect to update available specializations when service types change
  useEffect(() => {
    if (allSpecializations.length === 0) {
      return;
    }
    const mergedSpecs = new Set();
    formik.values.services_type.forEach((type) => {
      const specs = allSpecializations.find((s) => s.type_name === type)?.specializations || [];
      specs.forEach((spec) => mergedSpecs.add(spec));
    });

    const allowedSpecs = Array.from(mergedSpecs);
    const updatedSubservices = formik.values.subservices.filter((spec) => mergedSpecs.has(spec));
    
    setFilteredSpecializations(allowedSpecs);

    if (JSON.stringify(updatedSubservices) !== JSON.stringify(formik.values.subservices)) {
      formik.setFieldValue('subservices', updatedSubservices);
    }
  }, [formik.values.services_type, allSpecializations, formik.setFieldValue, formik.values.subservices]);

  // useEffect to keep addresses in sync
  useEffect(() => {
    if (sameAsHome) {
      formik.setFieldValue('homeaddress', formik.values.worklocation);
    }
  }, [sameAsHome, formik.values.worklocation, formik.setFieldValue]);

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, typeof value === 'string' ? value.split(',') : value);
  };

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setSameAsHome(isChecked);
    if (isChecked) {
      formik.setFieldValue('homeaddress', formik.values.worklocation);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Service Provider</DialogTitle>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await formik.validateForm();
          if (!formik.isValid) {
            console.log("Formik Validation Errors:", formik.errors);
          }
          formik.handleSubmit(e);
        }}
      >
        <DialogContent>
          <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Personal and Service Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  {...formik.getFieldProps('owner_name')}
                  label="Name"
                  fullWidth
                  margin="normal"
                  error={formik.touched.owner_name && Boolean(formik.errors.owner_name)}
                  helperText={formik.touched.owner_name && formik.errors.owner_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    multiple
                    name="services_type"
                    value={formik.values.services_type}
                    onChange={handleSelectChange}
                    input={<OutlinedInput label="Service Type" />}
                    sx={{ height: 'auto', minHeight: '45px' }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 90, overflowY: 'auto', p: 1.5 }}>
                        {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {servicesType.map((type) => (
                      <MenuItem key={type.id} value={type.type_name}>
                        {type.type_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Specializations</InputLabel>
                  <Select
                    sx={{ height: "auto", minHeight: "45px" }}
                    multiple
                    name="subservices"
                    value={formik.values.subservices}
                    onChange={handleSelectChange}
                    input={<OutlinedInput label="Specializations" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 90, overflowY: 'auto', p: 1.5 }}>
                        {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {filteredSpecializations.map((spec) => (
                      <MenuItem key={spec} value={spec}>
                        {spec}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...formik.getFieldProps('mobile_number')}
                  label="Mobile Number"
                  fullWidth
                  margin="normal"
                  disabled
                  error={formik.touched.mobile_number && Boolean(formik.errors.mobile_number)}
                  helperText={formik.touched.mobile_number && formik.errors.mobile_number}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...formik.getFieldProps('email')}
                  label="Email"
                  fullWidth
                  margin="normal"
                  disabled
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...formik.getFieldProps('password')}
                  label="Password"
                  placeholder="Enter new password to update"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  // You might want to change the helper text if the field is pre-filled
                  helperText={formik.touched.password ? formik.errors.password : "Enter a new password to update, or leave as is."}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <KeywordsInput
                  label="Service Related Keywords"
                  keywords={formik.values.keywords}
                  onChange={(newKeywords) => formik.setFieldValue('keywords', newKeywords)}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ backgroundColor: '#f3e5f5', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Location Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Shop / Work Address"
                  name="worklocation"
                  multiline
                  rows={4}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.worklocation}
                  fullWidth
                  margin="normal"
                  error={formik.touched.worklocation && Boolean(formik.errors.worklocation)}
                  helperText={formik.touched.worklocation && formik.errors.worklocation}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Home Address"
                  name="homeaddress"
                  multiline
                  rows={4}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.homeaddress}
                  disabled={sameAsHome}
                  fullWidth
                  margin="normal"
                  error={formik.touched.homeaddress && Boolean(formik.errors.homeaddress)}
                  helperText={formik.touched.homeaddress && formik.errors.homeaddress}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sameAsHome}
                      onChange={handleCheckboxChange}
                      sx={{
                          '& .MuiSvgIcon-root': { color: '#f44336' },
                          '&:hover .MuiSvgIcon-root': { color: '#e53935' },
                          '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' },
                      }}
                    />
                  }
                  label="Home Address is the same as Work Address"
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
             <Typography variant="h6" gutterBottom>
               Bank Details
             </Typography>
             <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('account_no')}
                    label="Account Number"
                    fullWidth
                    margin="normal"
                    error={formik.touched.account_no && Boolean(formik.errors.account_no)}
                    helperText={formik.touched.account_no && formik.errors.account_no}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('ifsc_code')}
                    label="IFSC Code"
                    fullWidth
                    margin="normal"
                    error={formik.touched.ifsc_code && Boolean(formik.errors.ifsc_code)}
                    helperText={formik.touched.ifsc_code && formik.errors.ifsc_code}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('passbook_name')}
                    label="Passbook Name"
                    fullWidth
                    margin="normal"
                    error={formik.touched.passbook_name && Boolean(formik.errors.passbook_name)}
                    helperText={formik.touched.passbook_name && formik.errors.passbook_name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('bank_name')}
                    label="Bank Name"
                    fullWidth
                    margin="normal"
                    error={formik.touched.bank_name && Boolean(formik.errors.bank_name)}
                    helperText={formik.touched.bank_name && formik.errors.bank_name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('branch_name')}
                    label="Branch Name"
                    fullWidth
                    margin="normal"
                    error={formik.touched.branch_name && Boolean(formik.errors.branch_name)}
                    helperText={formik.touched.branch_name && formik.errors.branch_name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('experiance')}
                    label="No of Years of Experience"
                    type='number'
                    fullWidth
                    margin="normal"
                    error={formik.touched.experiance && Boolean(formik.errors.experiance)}
                    helperText={formik.touched.experiance && formik.errors.experiance}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('pan_card_number')}
                    label="Pan Card No"
                    fullWidth
                    margin="normal"
                    error={formik.touched.pan_card_number && Boolean(formik.errors.pan_card_number)}
                    helperText={formik.touched.pan_card_number && formik.errors.pan_card_number}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...formik.getFieldProps('details')}
                    label="Additional Details"
                    fullWidth
                    margin="normal"
                    multiline
                    rows={4}
                    error={formik.touched.additional_details && Boolean(formik.errors.additional_details)}
                    helperText={formik.touched.additional_details && formik.errors.additional_details}
                  />
                </Grid>
             </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <MDButton
            type="submit"
            color="success"
            variant="contained"
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditServiceModal;